import React, { useMemo } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Collapse,
  Badge
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonAdd as PersonAddIcon,
  CalendarToday as CalendarIcon,
  ErrorOutline as ErrorIcon
} from '@material-ui/icons';
import moment from 'moment';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import {
  calculateSiteReviewPriority,
  getPrioritizedReviewList
} from './utils'

const useStyles = makeStyles(theme => ({
  alertsContainer: {
    marginBottom: theme.spacing(3),
  },
  alertCard: {
    height: '100%',
    position: 'relative',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[8],
    },
  },
  alertCardCritical: {
    borderLeft: `4px solid ${theme.palette.error.main}`,
    '&:hover': {
      backgroundColor: theme.palette.error.light + '08',
    },
  },
  alertCardWarning: {
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    '&:hover': {
      backgroundColor: theme.palette.warning.light + '08',
    },
  },
  alertCardInfo: {
    borderLeft: `4px solid ${theme.palette.info.main}`,
    '&:hover': {
      backgroundColor: theme.palette.info.light + '08',
    },
  },
  alertHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  alertIcon: {
    marginRight: theme.spacing(1),
  },
  alertIconCritical: {
    color: theme.palette.error.main,
  },
  alertIconWarning: {
    color: theme.palette.warning.main,
  },
  alertIconInfo: {
    color: theme.palette.info.main,
  },
  alertCount: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  alertCountCritical: {
    color: theme.palette.error.main,
  },
  alertCountWarning: {
    color: theme.palette.warning.main,
  },
  alertCountInfo: {
    color: theme.palette.info.main,
  },
  alertTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  alertDescription: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1),
  },
  expandButton: {
    padding: theme.spacing(0.5),
  },
  alertDetails: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  siteItem: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  siteAvatar: {
    width: 32,
    height: 32,
    fontSize: '0.75rem',
    backgroundColor: theme.palette.primary.main,
  },
  siteText: {
    margin: 0,
  },
  siteName: {
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  siteClient: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  actionButton: {
    marginTop: theme.spacing(1),
    textTransform: 'none',
  },
  overdueChip: {
    fontSize: '0.75rem',
    height: 24,
  },
  criticalOverdue: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  moderateOverdue: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  minorOverdue: {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.info.contrastText,
  },
}));



// Helper function to calculate next expected visit
const calculateNextExpectedVisit = (lastServiceVisit, frequency) => {
  if (!lastServiceVisit) return null;
  
  const lastVisit = moment(lastServiceVisit);
  const freq = frequency?.toLowerCase() || '';
  
  let nextVisit = moment(lastVisit);
  
  // Add appropriate time based on frequency
  switch (freq) {
    case 'daily':
      nextVisit.add(1, 'day');
      break;
    case 'every other day (m-w-f)':
      nextVisit.add(2, 'days');
      break;
    case 'twice a week':
      nextVisit.add(3, 'days'); // Approximate
      break;
    case 'weekly':
      nextVisit.add(1, 'week');
      break;
    case 'bi-weekly':
    case 'biweekly':
      nextVisit.add(2, 'weeks');
      break;
    case 'monthly':
      nextVisit.add(1, 'month');
      break;
    case 'quarterly':
      nextVisit.add(3, 'months');
      break;
    case 'semi-annually':
      nextVisit.add(6, 'months');
      break;
    case 'annually':
      nextVisit.add(1, 'year');
      break;
    default:
      return null;
  }
  
  return nextVisit;
};

// Helper function to get overdue severity
const getOverdueSeverity = (daysOverdue, frequency) => {
  const freq = frequency?.toLowerCase() || '';
  
  // Define thresholds based on frequency
  let criticalThreshold, moderateThreshold;
  
  switch (freq) {
    case 'daily':
    case 'every other day (m-w-f)':
      criticalThreshold = 3;
      moderateThreshold = 1;
      break;
    case 'twice a week':
    case 'weekly':
      criticalThreshold = 7;
      moderateThreshold = 3;
      break;
    case 'bi-weekly':
    case 'biweekly':
      criticalThreshold = 14;
      moderateThreshold = 7;
      break;
    case 'monthly':
      criticalThreshold = 30;
      moderateThreshold = 14;
      break;
    default:
      criticalThreshold = 14;
      moderateThreshold = 7;
  }
  
  if (daysOverdue >= criticalThreshold) return 'critical';
  if (daysOverdue >= moderateThreshold) return 'moderate';
  return 'minor';
};

const AlertCard = ({ 
  title, 
  description, 
  count, 
  severity, 
  icon, 
  sites, 
  actionLabel, 
  onAction,
  onSiteClick,
  expanded,
  onToggleExpanded 
}) => {
  const classes = useStyles();
  
  const severityClass = severity === 'critical' ? 'Critical' : 
                       severity === 'warning' ? 'Warning' : 'Info';

  return (
    <Card className={`${classes.alertCard} ${classes[`alertCard${severityClass}`]}`}>
      <CardContent>
        <div className={classes.alertHeader}>
          <Box display="flex" alignItems="center">
            <div className={`${classes.alertIcon} ${classes[`alertIcon${severityClass}`]}`}>
              {icon}
            </div>
            <Typography variant="h6" className={classes.alertTitle}>
              {title}
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center">
            <Typography className={`${classes.alertCount} ${classes[`alertCount${severityClass}`]}`}>
              {count}
            </Typography>
            {sites.length > 0 && (
              <IconButton 
                className={classes.expandButton}
                onClick={onToggleExpanded}
                size="small"
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>
        </div>
        
        <Typography className={classes.alertDescription}>
          {description}
        </Typography>
        
        {count > 0 && onAction && (
          <Button
            variant="outlined"
            size="small"
            className={classes.actionButton}
            onClick={onAction}
          >
            {actionLabel}
          </Button>
        )}
        
        <Collapse in={expanded && sites.length > 0}>
          <div className={classes.alertDetails}>
            <List dense>
              {sites.slice(0, 5).map((site, index) => (
                <ListItem 
                  key={index} 
                  button 
                  className={classes.siteItem}
                  onClick={() => onSiteClick && onSiteClick(site)}
                >
                  <ListItemAvatar>
                    <Avatar className={classes.siteAvatar}>
                      {site.name ? site.name.substring(0, 2).toUpperCase() : site.clientName?.substring(0, 2).toUpperCase() || 'JS'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    className={classes.siteText}
                    primary={
                      <Typography className={classes.siteName}>
                        {site.name || 'Unnamed Site'}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography className={classes.siteClient}>
                          {site.clientName}
                        </Typography>
                        {site.daysOverdue && (
                          <Chip
                            size="small"
                            label={`${site.daysOverdue} days overdue`}
                            className={`${classes.overdueChip} ${classes[`${site.overdueSeverity}Overdue`]}`}
                          />
                        )}
                        {site.reviewStatus && (
                          <Chip
                            size="small"
                            label={site.reviewStatus}
                            className={classes.overdueChip}
                            style={{ 
                              backgroundColor: site.neverReviewed ? '#f44336' : '#ff9800',
                              color: 'white'
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
              {sites.length > 5 && (
                <ListItem>
                  <ListItemText>
                    <Typography variant="body2" color="textSecondary">
                      ... and {sites.length - 5} more sites
                    </Typography>
                  </ListItemText>
                </ListItem>
              )}
            </List>
          </div>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const AlertsSystem = ({ 
  sites = [],
  siteReviews = [],
  onFilterApply,
  onSiteEdit,
  loading = false 
}) => {
  console.log('397...siteReviews', siteReviews)
  const classes = useStyles();
  const [expandedCards, setExpandedCards] = React.useState({
    biweekly: false,
    unassigned: false,
    overdue: false,
    review: false
  });

  // Calculate alerts
  const alerts = useMemo(() => {
    console.log('All frequencies in sites:', [...new Set(sites.map(s => s.frequency))]);
    // 1. Fix bi-weekly case sensitivity issue
    const biweeklySitesWithoutAssignment = sites.filter(site => {
      // Make case-insensitive comparison
      const frequency = site.frequency?.toLowerCase() || '';
      const isBiweekly = frequency.includes('bi-weekly') || frequency.includes('biweekly');
      const hasNoEvenOdd = !site.evenOrOddWeeks || site.evenOrOddWeeks === '';
      
      return isBiweekly && hasNoEvenOdd;
    });

    const biweeklyDebug = sites.filter(site => {
      const frequency = site.frequency?.toLowerCase() || '';
      return frequency.includes('bi-weekly') || frequency.includes('biweekly');
    });
    console.log('All bi-weekly sites:', biweeklyDebug.length);
    console.log('Bi-weekly sites without even/odd:', biweeklyDebug.filter(s => !s.evenOrOddWeeks || s.evenOrOddWeeks === '').length);
    
  
    // 2. Unassigned sites (this one should work already)
    const unassignedSites = sites.filter(site => 
      !site.crew || site.crew === null || site.crew === ''
    );
  
    // 3. Fix overdue sites calculation
    const overdueSites = sites.filter(site => {
      if (!site.lastServiceVisit || !site.frequency) return false;
      
      const nextExpected = calculateNextExpectedVisit(site.lastServiceVisit, site.frequency);
      if (!nextExpected) return false;
      
      const now = moment();
      return now.isAfter(nextExpected);
    }).map(site => {
      const nextExpected = calculateNextExpectedVisit(site.lastServiceVisit, site.frequency);
      const daysOverdue = moment().diff(nextExpected, 'days');
      const overdueSeverity = getOverdueSeverity(daysOverdue, site.frequency);
      
      return {
        ...site,
        daysOverdue,
        overdueSeverity,
        nextExpected
      };
    }).sort((a, b) => b.daysOverdue - a.daysOverdue);
  
    const overdueDebug = sites.filter(site => {
      if (!site.lastServiceVisit || !site.frequency) return false;
      const nextExpected = calculateNextExpectedVisit(site.lastServiceVisit, site.frequency);
      if (!nextExpected) return false;
      return moment().isAfter(nextExpected);
    });
    console.log('Overdue sites found:', overdueDebug.length);
    console.log('overdueDebug...',overdueDebug)
    const sitesDueForReview = getPrioritizedReviewList(sites, siteReviews)
    console.log('sits do...sitesDueForReview', sitesDueForReview)
    //   sites.filter(site => 
    //   isDueForReview(site, siteReviews)
    // ).map(site => {
    //   const siteReviewHistory = siteReviews.filter(review => 
    //     review.jobSite && review.jobSite._id === site._id
    //   ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
    //   const lastReviewDate = siteReviewHistory.length > 0 
    //     ? moment(siteReviewHistory[0].created_at)
    //     : null;
        
    //   const monthsSinceReview = lastReviewDate 
    //     ? moment().diff(lastReviewDate, 'months')
    //     : null;
    
    //   // Add priority calculation
    //   const reviewPriority = calculateSiteReviewPriority(site, sites);
    
    //   return {
    //     ...site,
    //     lastReviewDate,
    //     monthsSinceReview,
    //     neverReviewed: !lastReviewDate,
    //     reviewPriority
    //   };
    // }).sort((a, b) => {
    //   // Sort by priority score instead of just time
    //   return b.reviewPriority.totalScore - a.reviewPriority.totalScore;
    // });
 
    return {
      biweeklySitesWithoutAssignment,
      unassignedSites,
      overdueSites,
      sitesDueForReview
    };
  }, [sites, siteReviews]);

  const handleToggleExpanded = (cardType) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardType]: !prev[cardType]
    }));
  };

  const handleFilterToBiweeklyIssues = () => {
    onFilterApply({
      customerType: '',
      frequency: 'Bi-Weekly',
      preferredDay: '',
      crew: '',
      evenOrOddWeeks: 'Not Set',
      assignmentStatus: '',
      overdueService: '',
      overdueSiteReview: ''
    });
  };

  const handleFilterToUnassigned = () => {
    // Clear existing filters and apply new ones
    onFilterApply({
      customerType: '',
      frequency: '',
      preferredDay: '',
      crew: '',
      evenOrOddWeeks: '',
      assignmentStatus: 'Unassigned',
      overdueService: '',
      overdueSiteReview: ''
    });
  };

  const handleFilterToOverdue = () => {
    // Clear existing filters and apply new ones
    onFilterApply({
      customerType: '',
      frequency: '',
      preferredDay: '',
      crew: '',
      evenOrOddWeeks: '',
      assignmentStatus: '',
      overdueService: 'Yes',
      overdueSiteReview: ''
    });
  };

  const handleFilterToReviewDue = () => {
    onFilterApply({
      customerType: '',
      frequency: '',
      preferredDay: '',
      crew: '',
      evenOrOddWeeks: '',
      assignmentStatus: '',
      overdueService: '',
      overdueSiteReview: 'Yes'
    });
    
    // You could also scroll to the priority panel
    setTimeout(() => {
      const priorityPanel = document.querySelector('[data-component="priority-panel"]');
      if (priorityPanel) {
        priorityPanel.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };


  if (loading) {
    return null; // Or a loading skeleton
  }

  // Don't show alerts if no issues
  const totalIssues = alerts.biweeklySitesWithoutAssignment.length + 
                     alerts.unassignedSites.length + 
                     alerts.overdueSites.length +
                     alerts.sitesDueForReview.length;

  if (totalIssues === 0) {
    return (
      <Box className={classes.alertsContainer}>
        <Card className={classes.alertCard}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="center" py={2}>
              <CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} />
              <Typography variant="h6" style={{ color: '#4caf50' }}>
                All systems running smoothly! No alerts at this time.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <div className={classes.alertsContainer}>
      <Grid container spacing={3}>
        {/* Bi-weekly Sites Without Assignment Alert */}
        <Grid item xs={12} md={3}>
          <AlertCard
            title="Missing Even/Odd Assignment"
            description="Bi-weekly sites need even/odd week assignment for proper scheduling"
            count={alerts.biweeklySitesWithoutAssignment.length}
            severity="warning"
            icon={<CalendarIcon />}
            sites={alerts.biweeklySitesWithoutAssignment}
            actionLabel="Assign Even/Odd Weeks"
            onAction={handleFilterToBiweeklyIssues}
            onSiteClick={onSiteEdit}
            expanded={expandedCards.biweekly}
            onToggleExpanded={() => handleToggleExpanded('biweekly')}
          />
        </Grid>

        {/* Unassigned Sites Alert */}
        <Grid item xs={12} md={3}>
          <AlertCard
            title="Unassigned Sites"
            description="Sites without crew assignments need to be assigned for service delivery"
            count={alerts.unassignedSites.length}
            severity="critical"
            icon={<PersonAddIcon />}
            sites={alerts.unassignedSites}
            actionLabel="Assign Crews"
            onAction={handleFilterToUnassigned}
            onSiteClick={onSiteEdit}
            expanded={expandedCards.unassigned}
            onToggleExpanded={() => handleToggleExpanded('unassigned')}
          />
        </Grid>

        {/* Overdue Service Sites Alert */}
        <Grid item xs={12} md={3}>
          <AlertCard
            title="Overdue Services"
            description="Sites that have missed their scheduled service window"
            count={alerts.overdueSites.length}
            severity={alerts.overdueSites.some(s => s.overdueSeverity === 'critical') ? 'critical' : 'warning'}
            icon={<ScheduleIcon />}
            sites={alerts.overdueSites}
            actionLabel="Review Overdue"
            onAction={handleFilterToOverdue}
            onSiteClick={onSiteEdit}
            expanded={expandedCards.overdue}
            onToggleExpanded={() => handleToggleExpanded('overdue')}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <AlertCard
            title="Sites Due for Review"
            description="Sites that haven't been reviewed in the past 6 months"
            count={alerts.sitesDueForReview.length}
            severity="info"
            icon={<AssignmentIcon />}
            sites={alerts.sitesDueForReview.map(site => ({
              ...site,
              // Add review-specific display data
              reviewStatus: site.neverReviewed 
                ? 'Never reviewed' 
                : `${site.monthsSinceReview} months ago`
            }))}
            actionLabel="Schedule Reviews"
            onAction={handleFilterToReviewDue}
            onSiteClick={onSiteEdit}
            expanded={expandedCards.review}
            onToggleExpanded={() => handleToggleExpanded('review')}
          />
      </Grid>
      </Grid>
    </div>
  );
};

export default AlertsSystem;
